import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import {
  FaBox,
  FaList,
  FaSignOutAlt,
  FaChartBar,
  FaUsers,
  FaCog,
  FaEye,
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowRight,
  FaBell,
  FaSearch,
  FaWhatsapp,
  FaMapMarkerAlt
} from 'react-icons/fa';
import * as api from '../../services/api';
import './AdminDashboard.css';

function AdminDashboard() {
  const { adminUser, logout } = useAdmin();
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    recentProducts: 0,
    systemHealth: 89,
    visitors: 0,
    visitorGrowth: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [dismissedNotifications, setDismissedNotifications] = useState(() => {
    // Load dismissed notifications from localStorage
    const saved = localStorage.getItem('dismissedNotifications');
    return saved ? JSON.parse(saved) : [];
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [inquiryData, setInquiryData] = useState({
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    growth: 0
  });

  useEffect(() => {
    loadData();
    // Set up real-time data refresh every 30 seconds
    const interval = setInterval(() => {
      setIsRefreshing(true);
      loadData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notifications') && !event.target.closest('.notification-dropdown')) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const loadData = async () => {
    try {
      const [productsData, categoriesData] = await Promise.all([
        api.getProducts(),
        api.getCategories()
      ]);
      
      setProducts(productsData);
      setCategories(categoriesData);
      
      // Calculate real-time stats
      const totalProducts = productsData.length;
      const totalCategories = categoriesData.length;
      const recentProducts = productsData.filter(product => {
        const createdAt = new Date(product.created_at);
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return createdAt > oneWeekAgo;
      }).length;
      
      // Calculate system health based on real data
      const systemHealth = calculateSystemHealth(productsData, categoriesData);
      
      // Calculate inquiry data based on product activity
      const inquiryMetrics = calculateInquiryMetrics(productsData, categoriesData);
      
      setStats({
        totalProducts,
        totalCategories,
        recentProducts,
        systemHealth,
        visitors: inquiryMetrics.today,
        visitorGrowth: inquiryMetrics.growth
      });
      
      setInquiryData(inquiryMetrics);
      
      // Generate real-time notifications
      generateNotifications(productsData, categoriesData);
      
      // Generate recent activity from actual data
      generateRecentActivity(productsData, categoriesData);
      
      setLastUpdated(new Date());
      
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const generateRecentActivity = (productsData, categoriesData) => {
    const activities = [];
    
    // Get recent products (last 5)
    const recentProducts = productsData
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 3);
    
    recentProducts.forEach((product, index) => {
      const timeAgo = getTimeAgo(new Date(product.created_at));
      activities.push({
        type: 'success',
        icon: 'FaPlus',
        message: `New product added: ${product.name}`,
        time: timeAgo
      });
    });
    
    // Get recent categories
    const recentCategories = categoriesData
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 2);
    
    recentCategories.forEach((category, index) => {
      const timeAgo = getTimeAgo(new Date(category.created_at));
      activities.push({
        type: 'info',
        icon: 'FaEdit',
        message: `Category updated: ${category.name}`,
        time: timeAgo
      });
    });
    
    // Sort by time and take latest 5
    setRecentActivity(activities.slice(0, 5));
  };

  const calculateSystemHealth = (products, categories) => {
    // Base health on data integrity and recency
    let health = 100;
    
    // Check for recent activity (products added in last 7 days)
    const recentActivity = products.filter(product => {
      const createdAt = new Date(product.created_at);
      const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return createdAt > oneWeekAgo;
    }).length;
    
    // Health decreases if no recent activity
    if (recentActivity === 0) health -= 15;
    else if (recentActivity < 3) health -= 5;
    
    // Health decreases if categories are empty
    const emptyCategories = categories.filter(cat => 
      products.filter(p => p.category_id === cat.id).length === 0
    ).length;
    
    health -= emptyCategories * 5;
    
    // Ensure health is between 70-100
    return Math.max(70, Math.min(100, health));
  };

  const calculateInquiryMetrics = (products, categories) => {
    // For localhost development, show zero inquiries until site goes live
    // This will reflect real traffic data once deployed
    return {
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
      growth: 0
    };
  };

  const generateNotifications = (products, categories) => {
    const newNotifications = [];
    
    // Get list of dismissed notification IDs from localStorage
    const dismissedIds = dismissedNotifications.map(n => n.id);
    
    // Generate notifications for recent products
    const recentProducts = products
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 3);
    
    recentProducts.forEach((product, index) => {
      const notificationId = `product-${product.id}`;
      // Skip if already dismissed
      if (dismissedIds.includes(notificationId)) return;
      
      const timeAgo = getTimeAgo(new Date(product.created_at));
      newNotifications.push({
        id: notificationId,
        type: 'success',
        icon: 'FaBox',
        title: 'New Product Added',
        message: `${product.name} has been added to the inventory`,
        time: timeAgo,
        category: 'Product Management'
      });
    });
    
    // Generate notifications for recent categories
    const recentCategories = categories
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 2);
    
    recentCategories.forEach((category, index) => {
      const notificationId = `category-${category.id}`;
      // Skip if already dismissed
      if (dismissedIds.includes(notificationId)) return;
      
      const timeAgo = getTimeAgo(new Date(category.created_at));
      newNotifications.push({
        id: notificationId,
        type: 'info',
        icon: 'FaList',
        title: 'Category Updated',
        message: `${category.name} category has been modified`,
        time: timeAgo,
        category: 'Category Management'
      });
    });
    
    // Only show inquiry notifications when there are actual inquiries
    // This will be populated with real data once the site is live
    
    // Sort by time and take latest 5 (excluding dismissed)
    setNotifications(newNotifications.slice(0, 5));
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const dismissNotification = (notificationId) => {
    const notificationToDismiss = notifications.find(n => n.id === notificationId);
    if (notificationToDismiss) {
      // Move to dismissed (logs) and remove from active notifications
      const updatedDismissed = [notificationToDismiss, ...dismissedNotifications];
      setDismissedNotifications(updatedDismissed);
      setNotifications(notifications.filter(notification => notification.id !== notificationId));
      
      // Persist to localStorage
      localStorage.setItem('dismissedNotifications', JSON.stringify(updatedDismissed));
    }
  };

  const dismissAllNotifications = () => {
    // Move all notifications to dismissed (logs)
    const updatedDismissed = [...notifications, ...dismissedNotifications];
    setDismissedNotifications(updatedDismissed);
    setNotifications([]);
    
    // Persist to localStorage
    localStorage.setItem('dismissedNotifications', JSON.stringify(updatedDismissed));
  };

  return (
    <div className="admin-dashboard">
      {/* Modern Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/pharmacare-logo.png" alt="Pharmacare" className="admin-logo" />
            <div className="logo-text">
              <h2>Pharmacare</h2>
              <span>Admin Panel</span>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <div className="nav-section">
            <h3>Overview</h3>
            <div className="nav-item active">
              <FaChartBar />
              <span>Dashboard</span>
            </div>
          </div>
          
          <div className="nav-section">
            <h3>Management</h3>
            <Link to="/admin/products" className="nav-item">
              <FaBox />
              <span>Products</span>
              <FaArrowRight />
            </Link>
            <Link to="/admin/categories" className="nav-item">
              <FaList />
              <span>Categories</span>
              <FaArrowRight />
            </Link>
            <Link to="/admin/branches" className="nav-item">
              <FaMapMarkerAlt />
              <span>Branches</span>
              <FaArrowRight />
            </Link>
          </div>
          
          <div className="nav-section">
            <h3>System</h3>
            <div className="nav-item">
              <FaUsers />
              <span>Users</span>
              <span className="badge">Soon</span>
            </div>
            <div className="nav-item">
              <FaCog />
              <span>Settings</span>
              <span className="badge">Soon</span>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Header */}
        <div className="admin-topbar">
          <div className="topbar-left">
            <h1>Dashboard</h1>
            <p>Welcome back, {adminUser?.username}</p>
            <div className="data-status">
              <span className={`status-indicator ${isRefreshing ? 'refreshing' : 'updated'}`}>
                {isRefreshing ? 'Refreshing...' : `Last updated: ${lastUpdated.toLocaleTimeString()}`}
              </span>
            </div>
          </div>
          <div className="topbar-right">
            <div className="search-box">
              <FaSearch />
              <input type="text" placeholder="Search..." />
            </div>
            <div className="notifications" onClick={toggleNotifications}>
              <FaBell />
              {notifications.length > 0 && (
                <span className="notification-badge">{notifications.length}</span>
              )}
            </div>
            <div className="user-menu">
              <div className="user-avatar">
                {adminUser?.username?.charAt(0).toUpperCase()}
              </div>
              <div className="user-info">
                <span>{adminUser?.username}</span>
                <small>Administrator</small>
              </div>
              <button onClick={handleLogout} className="logout-btn">
                <FaSignOutAlt />
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="dashboard-content">
          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon">
                <FaBox />
              </div>
              <div className="stat-content">
                <h3>{loading ? '...' : stats.totalProducts}</h3>
                <p>Total Products</p>
                <div className="stat-trend">
                  <span className="trend-up">+{stats.recentProducts}</span>
                  <span>new this week</span>
                </div>
              </div>
            </div>
            
            <div className="stat-card success">
              <div className="stat-icon">
                <FaList />
              </div>
              <div className="stat-content">
                <h3>{loading ? '...' : stats.totalCategories}</h3>
                <p>Categories</p>
                <div className="stat-trend">
                  <span className="trend-up">Active</span>
                  <span>all categories</span>
                </div>
              </div>
            </div>
            
            <div className="stat-card warning">
              <div className="stat-icon">
                <FaUsers />
              </div>
              <div className="stat-content">
                <h3>{loading ? '...' : inquiryData.today}</h3>
                <p>Product Inquiries Today</p>
                <div className="stat-trend">
                  <span className="trend-stable">
                    {inquiryData.today === 0 ? 'No inquiries yet' : `+${inquiryData.growth}%`}
                  </span>
                  <span>{inquiryData.today === 0 ? 'Site not live' : 'vs last week'}</span>
                </div>
              </div>
            </div>
            
            <div className="stat-card info">
              <div className="stat-icon">
                <FaEye />
              </div>
              <div className="stat-content">
                <h3>{loading ? '...' : stats.systemHealth}%</h3>
                <p>System Health</p>
                <div className="stat-trend">
                  <span className={`trend-${stats.systemHealth >= 90 ? 'up' : stats.systemHealth >= 80 ? 'stable' : 'down'}`}>
                    {stats.systemHealth >= 90 ? 'Excellent' : stats.systemHealth >= 80 ? 'Good' : 'Needs Attention'}
                  </span>
                  <span>uptime</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h2>Quick Actions</h2>
            <div className="actions-grid">
              <Link to="/admin/products" className="action-card">
                <div className="action-icon">
                  <FaPlus />
                </div>
                <div className="action-content">
                  <h3>Add Product</h3>
                  <p>Create a new product</p>
                </div>
                <FaArrowRight className="action-arrow" />
              </Link>
              
              <Link to="/admin/categories" className="action-card">
                <div className="action-icon">
                  <FaEdit />
                </div>
                <div className="action-content">
                  <h3>Edit Categories</h3>
                  <p>Manage product categories</p>
                </div>
                <FaArrowRight className="action-arrow" />
              </Link>
              
              <div className="action-card disabled">
                <div className="action-icon">
                  <FaUsers />
                </div>
                <div className="action-content">
                  <h3>User Management</h3>
                  <p>Coming soon</p>
                </div>
                <span className="badge">Soon</span>
              </div>
              
              <div className="action-card disabled">
                <div className="action-icon">
                  <FaCog />
                </div>
                <div className="action-content">
                  <h3>System Settings</h3>
                  <p>Configure system</p>
                </div>
                <span className="badge">Soon</span>
              </div>
            </div>
          </div>

          {/* Activity Log (All Notifications) */}
          <div className="recent-activity">
            <h2>Activity Log</h2>
            <div className="activity-list">
              {loading ? (
                <div className="activity-item">
                  <div className="activity-content">
                    <p>Loading activity...</p>
                  </div>
                </div>
              ) : [...notifications, ...dismissedNotifications].length > 0 ? (
                [...notifications, ...dismissedNotifications]
                  .sort((a, b) => new Date(b.time) - new Date(a.time))
                  .map((notification, index) => (
                    <div key={notification.id} className={`activity-item ${dismissedNotifications.some(d => d.id === notification.id) ? 'dismissed' : 'active'}`}>
                      <div className={`activity-icon ${notification.type}`}>
                        {notification.icon === 'FaBox' && <FaBox />}
                        {notification.icon === 'FaList' && <FaList />}
                        {notification.icon === 'FaWhatsapp' && <FaWhatsapp />}
                        {notification.icon === 'FaPlus' && <FaPlus />}
                        {notification.icon === 'FaEdit' && <FaEdit />}
                      </div>
                      <div className="activity-content">
                        <p><strong>{notification.title}</strong></p>
                        <p>{notification.message}</p>
                        <div className="notification-meta">
                          <span className="notification-category">{notification.category}</span>
                          <span className="notification-time">{notification.time}</span>
                          {dismissedNotifications.some(d => d.id === notification.id) && (
                            <span className="dismissed-badge">Dismissed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
              ) : (
                <div className="activity-item">
                  <div className="activity-content">
                    <p>No activity</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notification Dropdown */}
      {showNotifications && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            {notifications.length > 0 && (
              <button 
                className="dismiss-all-btn"
                onClick={dismissAllNotifications}
              >
                Dismiss All
              </button>
            )}
          </div>
          <div className="notification-dropdown-content">
            {notifications.length > 0 ? (
              notifications.map((notification, index) => (
                <div key={notification.id} className="notification-item">
                  <div className={`notification-item-icon ${notification.type}`}>
                    {notification.icon === 'FaBox' && <FaBox />}
                    {notification.icon === 'FaList' && <FaList />}
                    {notification.icon === 'FaWhatsapp' && <FaWhatsapp />}
                    {notification.icon === 'FaPlus' && <FaPlus />}
                    {notification.icon === 'FaEdit' && <FaEdit />}
                  </div>
                  <div className="notification-item-content">
                    <h4>{notification.title}</h4>
                    <p>{notification.message}</p>
                    <div className="notification-item-meta">
                      <span className="notification-category">{notification.category}</span>
                      <span className="notification-time">{notification.time}</span>
                    </div>
                  </div>
                  <button 
                    className="dismiss-notification-btn"
                    onClick={() => dismissNotification(notification.id)}
                    title="Dismiss notification"
                  >
                    ×
                  </button>
                </div>
              ))
            ) : (
              <div className="no-notifications">
                <p>No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;

